import { useEffect } from 'react'
import type React from 'react'

export type SeoAlternate = { hreflang: string; href: string }

type SeoHeadProps = {
  title: string
  description: string
  canonicalUrl: string
  ogTitle?: string
  ogDescription?: string
  ogUrl?: string
  twitterTitle?: string
  twitterDescription?: string
  noindex?: boolean
  jsonLd?: unknown[]
  /** Reciprocal hreflang alternates (ar/en/fr/x-default). */
  alternates?: SeoAlternate[]
  /** <html lang> for this page. */
  htmlLang?: string
  /** <html dir> for this page. */
  dir?: 'ltr' | 'rtl'
  /** og:locale (e.g. ar_AR). */
  ogLocale?: string
  /** og:locale:alternate values. */
  ogLocaleAlternates?: string[]
}

const upsertMeta = (selector: string, create: () => HTMLMetaElement, value: string) => {
  let element = document.head.querySelector<HTMLMetaElement>(selector)
  if (!element) {
    element = create()
    document.head.appendChild(element)
  }
  element.setAttribute('content', value)
}

const upsertLink = (rel: string, href: string) => {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]:not([hreflang])`)
  if (!element) {
    element = document.createElement('link')
    element.setAttribute('rel', rel)
    document.head.appendChild(element)
  }
  element.setAttribute('href', href)
}

export const SeoHead: React.FC<SeoHeadProps> = ({
  title,
  description,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogUrl,
  twitterTitle,
  twitterDescription,
  noindex,
  jsonLd,
  alternates,
  htmlLang,
  dir,
  ogLocale,
  ogLocaleAlternates,
}) => {
  useEffect(() => {
    document.title = title
    if (htmlLang) document.documentElement.lang = htmlLang
    if (dir) document.documentElement.dir = dir
    upsertMeta('meta[name="description"]', () => {
      const element = document.createElement('meta')
      element.setAttribute('name', 'description')
      return element
    }, description)
    upsertMeta('meta[name="robots"]', () => {
      const element = document.createElement('meta')
      element.setAttribute('name', 'robots')
      return element
    }, noindex ? 'noindex, nofollow' : 'index, follow')
    upsertLink('canonical', canonicalUrl)

    const openGraph: Record<string, string> = {
      'og:type': 'website',
      'og:title': ogTitle || title,
      'og:description': ogDescription || description,
      'og:url': ogUrl || canonicalUrl,
      'og:site_name': 'NexusWorkforce360',
    }
    if (ogLocale) openGraph['og:locale'] = ogLocale

    Object.entries(openGraph).forEach(([property, value]) => {
      upsertMeta(`meta[property="${property}"]`, () => {
        const element = document.createElement('meta')
        element.setAttribute('property', property)
        return element
      }, value)
    })

    // Replace managed og:locale:alternate tags.
    document.head
      .querySelectorAll('meta[property="og:locale:alternate"][data-managed-seo="1"]')
      .forEach((node) => node.remove())
    ;(ogLocaleAlternates || []).forEach((value) => {
      const element = document.createElement('meta')
      element.setAttribute('property', 'og:locale:alternate')
      element.setAttribute('content', value)
      element.setAttribute('data-managed-seo', '1')
      document.head.appendChild(element)
    })

    const twitter: Record<string, string> = {
      'twitter:card': 'summary_large_image',
      'twitter:title': twitterTitle || ogTitle || title,
      'twitter:description': twitterDescription || ogDescription || description,
    }

    Object.entries(twitter).forEach(([name, value]) => {
      upsertMeta(`meta[name="${name}"]`, () => {
        const element = document.createElement('meta')
        element.setAttribute('name', name)
        return element
      }, value)
    })

    // Replace managed hreflang alternate links.
    document.head
      .querySelectorAll('link[rel="alternate"][hreflang][data-managed-seo="1"]')
      .forEach((node) => node.remove())
    ;(alternates || []).forEach((alt) => {
      const link = document.createElement('link')
      link.setAttribute('rel', 'alternate')
      link.setAttribute('hreflang', alt.hreflang)
      link.setAttribute('href', alt.href)
      link.setAttribute('data-managed-seo', '1')
      document.head.appendChild(link)
    })

    const scriptId = 'landing-json-ld'
    const existing = document.getElementById(scriptId)
    if (existing) existing.remove()
    if (jsonLd && jsonLd.length > 0) {
      const script = document.createElement('script')
      script.id = scriptId
      script.type = 'application/ld+json'
      script.text = JSON.stringify(jsonLd.length === 1 ? jsonLd[0] : { '@context': 'https://schema.org', '@graph': jsonLd })
      document.head.appendChild(script)
    }
  }, [
    canonicalUrl, description, jsonLd, noindex, ogDescription, ogTitle, ogUrl, title,
    twitterDescription, twitterTitle, alternates, htmlLang, dir, ogLocale, ogLocaleAlternates,
  ])

  return null
}
